import { IPublicBuyer } from 'domain/entities/Buyer/Buyer';
import { mqMin } from 'utils/responsive';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { MATCH_STATUSES } from 'domain/entities/Match/Match';
import PointIcon from 'ui/images/point.svg';
import BsPeopleIcon from 'ui/images/bs_people.svg';
import BsGraphUpIcon from 'ui/images/bs_graph_up.svg';
import dayjs from 'dayjs';
import { ColorLabel } from 'ui/components/elements/ColorLabel/ColorLabel';

interface BuyerCardProps {
    buyer: IPublicBuyer;
}

const Wrapper = styled.div`
    position: relative;
    width: 100%;
    background-color: #ffffff;
    border-radius: 0.75rem;
    min-height: 16rem;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    padding: 2.5rem 1rem 1.5rem 1.5rem;
    height: 100%;
    overflow: hidden;
    box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.1);

    &:hover {
        box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.2);
    }

    ${mqMin('SP')} {
        min-width: 300px;
        width: calc(50% - 10px);
    }

    ${mqMin('NARROW')} {
        width: calc(33.33% - 20px);
    }
`;

const ContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.7rem;
    flex: 1;
    height: 100%;
    box-sizing: border-box;
`;

const Title = styled.div`
    font-size: var(--font-medium);
    font-weight: 600;
    letter-spacing: 0.025em;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    gap: 0.25rem;

    img {
        width: 16px;
        height: 16px;
    }
`;

const LabelContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: center;
    padding: 0.25rem 0;
    margin-top: 0.125rem;
`;

const Label = styled.div`
    border-radius: 0.875rem;
    padding: 0.1875rem 0.7rem;
    font-size: 12px;
    font-weight: var(--font-bold);
    display: inline-block;
`;

const GrayLabel = styled(Label)`
    border-radius: 4px;
    background-color: #f3f3f3;
    align-items: center;
    font-size: 12px;
    color: #222222;
    font-weight: 500;
`;

const CountText = styled.span`
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
    margin-left: 0.2rem;
`;

const ItemContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    border-top: 2px solid #f3f3f3;
    padding-top: 0.5rem;
`;

const BuyerLabel = styled.div`
    border-radius: 0.75rem 0 8px 0;
    padding: 0.4rem 0.825rem;
    font-weight: 600;
    font-size: var(--font-large);
    line-height: 1;
    letter-spacing: 0.025em;
    white-space: nowrap;
    width: fit-content;
    color: white;
    background: var(--main-color-blue);
    position: absolute;
    top: -1px;
    left: -1px;
`;

const StatusBadge = styled.div`
    border-radius: 30px;
    padding: 5px 8px;
    font-weight: 700;
    font-size: 12px;
    line-height: 14.4px;
    letter-spacing: 0%;
    white-space: nowrap;
    color: white;
    background-color: #ee793f;
`;

const LabelsContainer = styled.div`
    position: absolute;
    top: 7px;
    right: 15px;
    display: flex;
    gap: 5px;
    align-items: center;
`;

const CreatedAt = styled.div`
    font-size: 14px;
    color: #222222;
    font-weight: 600;
`;

const SmallLabel = styled.div`
    font-size: 14px;
    color: var(--dark-blue);
    font-weight: 700;
`;

const NormalLabel = styled.div`
    font-size: 14px;
    color: var(--dark-blue);
    font-weight: 700;
    margin-top: 5px;
`;

const BlueTextContainer = styled.div`
    background-color: var(--soft-blue);
    padding: 8px 10px;
    width: 100%;
    font-size: 16px;
    color: var(--dark-blue);
    font-weight: 700;
    gap: 5px;
    display: flex;
    flex-direction: column;
    margin-bottom: 5px;
`;

const BottomContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: auto;
    padding-top: 10px;
    border-top: 2px solid #f3f3f3;
`;

const FlexContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 8px;
    align-items: center;
`;

export const BuyerCard: React.FC<BuyerCardProps> = ({ buyer }) => {
    const navigate = useNavigate();

    return (
        <Wrapper onClick={() => navigate(`/agent/buyers/${buyer.id}/`)}>
            <LabelsContainer>
                {buyer.is_sole_proprietor && (
                    <ColorLabel
                        color="#000"
                        backgroundColor="var(--cream)"
                        borderColor="#000"
                        fontWeight="normal"
                        borderRadius="14px"
                    >
                        個人事業主
                    </ColorLabel>
                )}
                {buyer?.match_status && (
                    <StatusBadge>
                        {buyer.match_status === MATCH_STATUSES.MATCH_STATUS_WAITING_ACCEPT && 'オファー済み'}
                        {buyer.match_status === MATCH_STATUSES.MATCH_STATUS_CONNECTED && 'マッチング済み'}
                        {buyer.match_status === MATCH_STATUSES.MATCH_STATUS_EXPIRED && 'オファー期限切れ'}
                        {buyer.match_status === MATCH_STATUSES.MATCH_STATUS_DECLINED && 'オファー辞退'}
                    </StatusBadge>
                )}
            </LabelsContainer>

            {buyer?.label && <BuyerLabel>{buyer.label}</BuyerLabel>}

            {buyer.target_company_purchase_amount && (
                <BlueTextContainer>
                    <SmallLabel>買収希望金額</SmallLabel>
                    <FlexContainer>
                        {buyer.target_company_purchase_amount}
                        {buyer.has_acquisition_experience === '1' && (
                            <ColorLabel
                                color="#008000"
                                backgroundColor="#d3ffa8"
                                borderColor="#008000"
                                padding="0.2rem"
                            >
                                買収実績1件あり！
                            </ColorLabel>
                        )}
                        {buyer.has_acquisition_experience === '2' && (
                            <ColorLabel
                                color="#EA5419"
                                backgroundColor="#FFEBD8"
                                borderColor="#EA5419"
                                padding="0.2rem"
                                borderWidth="2px"
                            >
                                買収実績2件以上！
                            </ColorLabel>
                        )}
                    </FlexContainer>
                </BlueTextContainer>
            )}
            <ContentWrapper>
                <ItemContainer>
                    <Title>
                        <img src={BsPeopleIcon} alt="人アイコン" />
                        買収希望案件業種
                    </Title>
                    <LabelContainer>
                        {buyer.target_company_industries && (
                            <>
                                {buyer.target_company_industries.slice(0, 5).map((industry: string) => (
                                    <GrayLabel key={industry}>{industry}</GrayLabel>
                                ))}
                                {buyer.target_company_industries.length > 5 && (
                                    <CountText>+{buyer.target_company_industries.length - 5}</CountText>
                                )}
                            </>
                        )}
                    </LabelContainer>
                </ItemContainer>
                <ItemContainer>
                    <Title>
                        <img src={PointIcon} alt="ピンアイコン" />
                        買収希望案件エリア
                    </Title>
                    <LabelContainer>
                        {buyer.area_name && (
                            <>
                                {buyer.area_name.slice(0, 5).map((area: string) => (
                                    <GrayLabel key={area}>{area}</GrayLabel>
                                ))}
                                {buyer.area_name.length > 5 && <CountText>+{buyer.area_name.length - 5}</CountText>}
                            </>
                        )}
                    </LabelContainer>
                </ItemContainer>
                {buyer.target_company_employee_size && (
                    <ItemContainer>
                        <Title>
                            <img src={BsPeopleIcon} alt="人アイコン" />
                            買収希望案件企業の従業員数
                        </Title>
                        <NormalLabel>{buyer.target_company_employee_size}</NormalLabel>
                    </ItemContainer>
                )}
                {buyer.target_company_revenue_scale && (
                    <ItemContainer>
                        <Title>
                            <img src={BsGraphUpIcon} alt="グラフアイコン" />
                            買収希望案件の売上高
                        </Title>
                        <NormalLabel>{buyer.target_company_revenue_scale}</NormalLabel>
                    </ItemContainer>
                )}
                {buyer.target_company_profit_level && (
                    <ItemContainer>
                        <Title>
                            <img src={BsGraphUpIcon} alt="グラフアイコン" />
                            買収希望案件の営業利益
                        </Title>
                        <NormalLabel>{buyer.target_company_profit_level}</NormalLabel>
                    </ItemContainer>
                )}

                <BottomContainer>
                    <CreatedAt>最終更新日：{dayjs(buyer.updated_at).format('YYYY/MM/DD')}</CreatedAt>
                </BottomContainer>
            </ContentWrapper>
        </Wrapper>
    );
};
